import { useEffect, useState } from 'react'

import { AdjustTotalPeopleModal } from './components/modals/AdjustTotalPeopleModal'
import { BudgetPreviewsWrapper } from '@components/shared/BudgetPreviewsWrapper'
import { Button } from '@components/shared/Button'
import { PreviewsStatus } from '@components/shared/BudgetPreviewsStatus/types'
import { CollaboratorsSectionComponent } from './components/CollaboratorsSection'
import { consolidatedPeople } from './components/PeopleBigNumbers'
import { PreviewCardSkeleton } from './components/PreviewCard/PreviewCardSkeleton'
import { currencyMask } from '@utils/masks/currencyMask'
import { detailedSummaryPeopleMock } from './helpers/mocks'
import { EditDetailedSummaryPeopleModal } from './components/modals/EditDetailedSummaryPeopleModal'
import { emotionTheme } from '@styles/theme'
import { PageWrapper } from '@components/shared/PageWrapper'
import { parseDetailedSummaryPeople } from './helpers/parseDetailedSummaryPeople'
import { parseWeeklyPreviewsGrid } from './helpers/parseWeeklyPreviewsGrid'
import { PreviewWrapper } from '@components/shared/PreviewWrapper'
import { ReturnModal } from '@components/modals/ReturnModal'
import { Separator } from '@components/shared/Separator'
import { Slider } from '@components/shared/Slider'
import { Table } from '@components/shared/Table'
import { TableGrouped } from '@components/shared/TableGrouped'
import { Title } from '@components/shared/Title'
import { useMediaQuery } from '@mui/material'
import { usePermission } from '@hooks/usePermission'
import { useUser } from '@hooks/useUser'
import { PREVIEW_ACCOUNTS, PreviewHistoryDrawer } from '@components/shared/PreviewHistoryDrawer'
import { PageIds } from '@hooks/useLayout/types'
// import { PROFILE_GU } from '@pages/Preview/helpers/previewGridValidationActions'
import { usePeople } from './context'
import { WarningBanner } from '@components/shared/WarningBanner'
import { WarningIcon } from '@phosphor-icons/react'

import { GENERAL_ADJUST_TOOLTIP, GroupInfoTooltip } from './helpers/groupTooltips'
import { formattedDatePtBrToIso } from '@utils/dates/formattedDatePtBrToIso'
import { unMaskCurrency } from '@utils/masks/unMaskCurrency'

import type { AdjustTotalPeopleModalFormProps } from './components/modals/AdjustTotalPeopleModal/form/types'
import type { DetailedSummaryItemProps, DetailedSummaryGroupProps, PeopleFiltersRequestProps } from './context/types'

import * as S from './styles'

export const People = () => {
  const { filiData, userPermissionsData } = useUser()
  const { canEdit: canEditPage } = usePermission(PageIds.PEOPLE)
  // const profileName = userPermissionsData?.profile?.name
  // const isGUProfile = profileName === PROFILE_GU

  const {
    getPeoplePreviewDetails,
    getDetailedSummary,
    editDetailedSummary,
    getCollaboratorsTotal,
    bigNumbersData,
    weeklyPreviewData,
    isBigNumbersLoading,
    isWeeklyPreviewLoading,
    detailedSummaryData,
    isDetailedSummaryLoading,
    generalAdjustPeople,
    isGeneralAdjustLoading,
    releasePreviewPeople,
    reversePreviewPeople,
    isReleasePreviewPeopleLoading,
    isReversePreviewPeopleLoading
  } = usePeople()
  const canEditFromPreview = bigNumbersData?.canEdit === true
  const canEditFinancial = canEditFromPreview && canEditPage
  const isMobile = useMediaQuery(`(max-width: ${emotionTheme.screenSizes.laptop})`)

  const [editModalOpen, setEditModalOpen] = useState(false)
  const [selectedItem, setSelectedItem] = useState<DetailedSummaryItemProps | null>(null)
  const [selectedGroup, setSelectedGroup] = useState<DetailedSummaryGroupProps | null>(null)
  const [historyWeekNumber, setHistoryWeekNumber] = useState<number | null>(null)
  const [openReleasePreviewModal, setOpenReleasePreviewModal] = useState(false)
  const [openReversePreviewModal, setOpenReversePreviewModal] = useState(false)
  const [openAdjustTotalModal, setOpenAdjustTotalModal] = useState(false)
  const currentPreview = weeklyPreviewData?.items.find(
    (item) => item.previewNumber === bigNumbersData?.currentPreviewNumber
  )
  const currentPreviewStatus = currentPreview?.previewStatus.code
  const canAdjustTotalPeople =
    canEditFinancial &&
    !!currentPreviewStatus &&
    [PreviewsStatus.NOT_STARTED, PreviewsStatus.IN_PROGRESS, PreviewsStatus.RETURNED_GU].some(
      (status) => status === currentPreviewStatus
    )
  const showAdjustTotalPeopleButton = !!currentPreviewStatus

  // Refetch quando a competência (período/data) ou centro de lucro mudar no header
  const competenceKey =
    filiData?.filiCode && filiData?.startPeriod ? `${filiData.filiCode}-${filiData.startPeriod}` : null

  useEffect(() => {
    if (!competenceKey || !filiData?.filiCode || !filiData?.startPeriod) return

    const params: PeopleFiltersRequestProps = {
      filiCode: filiData.filiCode,
      competenceDate: filiData.startPeriod
    }

    getPeoplePreviewDetails(params)
    getDetailedSummary(params)
    getCollaboratorsTotal({
      filiCode: filiData.filiCode,
      referenceDate: filiData.startPeriod
    })
  }, [
    competenceKey,
    filiData?.filiCode,
    filiData?.startPeriod,
    getPeoplePreviewDetails,
    getDetailedSummary,
    getCollaboratorsTotal
  ])

  const handleEditItem = (item: DetailedSummaryItemProps, group: DetailedSummaryGroupProps) => {
    if (!canEditFinancial) return

    setSelectedItem(item)
    setSelectedGroup(group)
    setEditModalOpen(true)
  }

  const handleSaveItem = async (data: { item: DetailedSummaryItemProps; amount: number }) => {
    if (!canEditFinancial) return
    if (!filiData?.filiCode || !filiData?.startPeriod) return

    await editDetailedSummary({
      filiCode: filiData.filiCode,
      competenceDate: filiData.startPeriod,
      accountingCode: data.item.accountCode,
      accountingDescription: data.item.description,
      amount: data.amount,
      isDiscount: data.item.isDiscount
    })

    setEditModalOpen(false)
    setSelectedItem(null)
    setSelectedGroup(null)
  }

  const handleAdjustTotalSubmit = async (formData: AdjustTotalPeopleModalFormProps) => {
    if (!canAdjustTotalPeople) return false
    if (!filiData?.filiCode || !filiData?.startPeriod) return false

    const isoDate = formattedDatePtBrToIso(formData.date)

    return generalAdjustPeople({
      filiCode: filiData.filiCode,
      referenceDate: filiData.startPeriod,
      entryDescription: 'Ajuste Geral',
      adjustmentDate: isoDate ? new Date(isoDate).toISOString() : new Date().toISOString(),
      totalAmount: unMaskCurrency(formData.totalValue),
      observations: formData.observations
    })
  }

  const renderDetailedSummaryFixedRow = (label: string, value: number, tooltip?: string) => (
    <div style={{ display: 'flex', width: '100%', background: '#747495', alignItems: 'center', height: '48px' }}>
      <div
        style={{
          flex: 1,
          paddingLeft: '16px',
          color: 'white',
          fontWeight: 600,
          fontSize: '16px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}
      >
        {label}
        {tooltip && <GroupInfoTooltip title={tooltip} color="#FFFFFF" />}
      </div>
      <div
        style={{
          minWidth: '15rem',
          paddingRight: '12px',
          color: 'white',
          fontWeight: 600,
          fontSize: '16px',
          textAlign: 'right'
        }}
      >
        {currencyMask(value || 0)}
      </div>
      <div style={{ minWidth: '5rem', maxWidth: '5rem' }} />
    </div>
  )

  return (
    <PageWrapper
      breadcrumbData={[
        { label: 'Gestão Financeira' },
        {
          label: `Pessoal / ${filiData?.filiCode === 'ALL' ? 'Todos' : `${filiData?.filiCode} - ${filiData?.filiName}`}`
        }
      ]}
    >
      <WarningBanner message={bigNumbersData?.corporateFoodSegmentMessage} />

      <Title label="Pessoal" marginBottom={32} />

      <BudgetPreviewsWrapper>
        {isBigNumbersLoading ? (
          isMobile ? (
            <Slider
              items={[
                <PreviewCardSkeleton key="1" />,
                <PreviewCardSkeleton key="2" />,
                <PreviewCardSkeleton key="3" />
              ]}
            />
          ) : (
            <PreviewWrapper>
              {[<PreviewCardSkeleton key="1" />, <PreviewCardSkeleton key="2" />, <PreviewCardSkeleton key="3" />]}
            </PreviewWrapper>
          )
        ) : isMobile ? (
          <Slider items={consolidatedPeople({ data: bigNumbersData })} />
        ) : (
          <PreviewWrapper>{consolidatedPeople({ data: bigNumbersData })}</PreviewWrapper>
        )}

        <Table
          data={parseWeeklyPreviewsGrid({ data: weeklyPreviewData?.items || [], onViewHistory: setHistoryWeekNumber })}
          isLoading={isWeeklyPreviewLoading}
        />
      </BudgetPreviewsWrapper>

      <Separator height={32} />

      <CollaboratorsSectionComponent canEdit={canEditFinancial} />

      <Title label="Resumo Detalhado" marginBottom={24} marginTop={32} />

      <TableGrouped
        data={parseDetailedSummaryPeople({
          data: detailedSummaryData || detailedSummaryPeopleMock,
          onEdit: handleEditItem,
          canEdit: canEditFinancial,
          profileName: userPermissionsData?.profile?.name
        })}
        fixedRow={{
          element: [
            renderDetailedSummaryFixedRow('Total custos pessoal', detailedSummaryData?.grandTotal || 0),
            renderDetailedSummaryFixedRow(
              'Ajuste Geral de Prévia',
              detailedSummaryData?.generalAdjustAmount || 0,
              GENERAL_ADJUST_TOOLTIP
            )
          ]
        }}
        isLoading={isDetailedSummaryLoading}
      />

      <S.Buttons>
        {bigNumbersData?.allowRevertPreview && (
          <Button
            width={isMobile ? 'auto' : 165}
            colorType="outlineBlue"
            onClick={() => setOpenReversePreviewModal(true)}
          >
            Reverter Prévia
          </Button>
        )}

        {showAdjustTotalPeopleButton && (
          <Button
            width={isMobile ? 'auto' : 210}
            colorType="solidBlue"
            onClick={() => setOpenAdjustTotalModal(true)}
          >
            Ajustar Total - Pessoal
          </Button>
        )}

        {canEditFinancial && bigNumbersData?.allowReleasePreview && (
          <Button
            width={isMobile ? 'auto' : 165}
            colorType="solidBlue"
            onClick={() => setOpenReleasePreviewModal(true)}
          >
            Liberar Prévia
          </Button>
        )}
      </S.Buttons>

      <AdjustTotalPeopleModal
        open={openAdjustTotalModal}
        onClose={setOpenAdjustTotalModal}
        onSubmitForm={handleAdjustTotalSubmit}
        isLoading={isGeneralAdjustLoading}
        canEdit={canAdjustTotalPeople}
      />

      <EditDetailedSummaryPeopleModal
        open={editModalOpen}
        onClose={setEditModalOpen}
        item={selectedItem}
        group={selectedGroup}
        onSave={handleSaveItem}
        isLoading={isDetailedSummaryLoading}
        canEdit={canEditFinancial}
      />

      <ReturnModal
        open={openReleasePreviewModal}
        headerIcon={<WarningIcon size={24} weight="bold" />}
        headerTitle="Liberar Prévia"
        message={`Tem certeza de que deseja liberar a ${bigNumbersData?.currentPreviewNumber}ª Prévia de Pessoal?`}
        cancelButtonText="Cancelar"
        confirmButtonText="Confirmar"
        onClose={setOpenReleasePreviewModal}
        isLoading={isReleasePreviewPeopleLoading}
        onConfirm={() => {
          if (!canEditFinancial) return

          releasePreviewPeople({
            filiCode: filiData?.filiCode!,
            competenceDate: filiData?.startPeriod!
          }).then((isSuccess) => isSuccess && setOpenReleasePreviewModal(false))
        }}
      />

      <ReturnModal
        open={openReversePreviewModal}
        headerIcon={<WarningIcon size={24} weight="bold" />}
        headerTitle="Reverter Prévia"
        message={`Tem certeza de que deseja reverter a ${bigNumbersData?.currentPreviewNumber}ª Prévia de Pessoal?`}
        cancelButtonText="Cancelar"
        confirmButtonText="Confirmar"
        onClose={setOpenReversePreviewModal}
        isLoading={isReversePreviewPeopleLoading}
        onConfirm={() => {
          reversePreviewPeople({
            filiCode: filiData?.filiCode!,
            competenceDate: filiData?.startPeriod!
          }).then((isSuccess) => isSuccess && setOpenReversePreviewModal(false))
        }}
      />

      <PreviewHistoryDrawer
        pageLabel="Pessoal"
        accounts={[PREVIEW_ACCOUNTS.people]}
        weekNumber={historyWeekNumber}
        onClose={() => setHistoryWeekNumber(null)}
      />
    </PageWrapper>
  )
}
